// src/pages/FicheFrais/components/IndemnitiesCard.jsx
import React, { useMemo } from "react";
import SectionCard from "../ui/SectionCard";
import Field from "../ui/Field";
import Button from "../../../../components/Button/Button.jsx";
import { FaHandHoldingUsd, FaSave } from "react-icons/fa";

const asMoney = (v) => {
  const n = Number(String(v ?? "").replace(",", "."));
  return Number.isFinite(n) ? n : 0;
};

export default function IndemnitiesCard({
  draft,
  setDraft,
  saving,
  onSave,
  disabled,
}) {
  // ✅ Déduit le "mode" depuis les champs DB
  const indemnityMode = useMemo(() => {
    const tenue = asMoney(draft?.indemnite_tenue);
    const rep = asMoney(draft?.indemnite_representation);

    if (rep > 0) return "representation";
    if (tenue > 0) return "tenue";
    return "none";
  }, [draft?.indemnite_tenue, draft?.indemnite_representation]);

  const totalIndemnites = useMemo(() => {
    return (asMoney(draft?.indemnite_tenue) + asMoney(draft?.indemnite_representation)).toFixed(2);
  }, [draft?.indemnite_tenue, draft?.indemnite_representation]);

  const applyMode = (mode) => {
    setDraft((p) => {
      if (mode === "tenue") {
        return { ...p, indemnite_tenue: "25", indemnite_representation: "0" };
      }
      if (mode === "representation") {
        return { ...p, indemnite_tenue: "0", indemnite_representation: "40" };
      }
      return { ...p, indemnite_tenue: "0", indemnite_representation: "0" };
    });
  };

  return (
    <SectionCard
      id="indemnites"
      title="Indemnité"
      subtitle="Choisissez une seule indemnité : Tenue (25€) OU Représentation (40€)"
      icon={<FaHandHoldingUsd />}
      actions={
        <div className="flex flex-col gap-2">
          <Button
            text={saving ? "Enregistrement..." : "Enregistrer la fiche"}
            variant="primary"
            size="sm"
            onClick={onSave}
            icon={FaSave}
            disabled={disabled || saving}
            type="button"
          />
        </div>
      }
    >
      {disabled ? (
        <div className="rounded-xl p-4 bg-amber-50 text-amber-900 ring-1 ring-amber-200">
          Sélectionne d’abord un <strong>événement</strong> (et enregistre la fiche) pour continuer.
        </div>
      ) : null}

      <div className={`mt-4 grid gap-4 ${disabled ? "opacity-60 pointer-events-none" : ""}`}>
        {/* ✅ Choix exclusif */}
        <div className="rounded-2xl ring-1 ring-slate-200 bg-white p-4">
          <div className="text-sm font-semibold text-slate-800 mb-3">
            Indemnité (une seule)
          </div>

          <div className="grid gap-2">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="radio"
                name="indemnity_mode"
                checked={indemnityMode === "none"}
                onChange={() => applyMode("none")}
              />
              <span className="text-slate-700">Aucune</span>
            </label>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="radio"
                name="indemnity_mode"
                checked={indemnityMode === "tenue"}
                onChange={() => applyMode("tenue")}
              />
              <span className="text-slate-700">
                Tenue <strong>(25 €)</strong>
              </span>
            </label>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="radio"
                name="indemnity_mode"
                checked={indemnityMode === "representation"}
                onChange={() => applyMode("representation")}
              />
              <span className="text-slate-700">
                Représentation <strong>(40 €)</strong>
              </span>
            </label>
          </div>

          {/* ✅ Total (simple) */}
          <div className="mt-4 rounded-xl ring-1 ring-slate-200 bg-slate-50 p-3">
            <div className="text-xs uppercase tracking-wide text-slate-500">
              Total indemnité
            </div>
            <div className="mt-1 text-lg font-semibold text-slate-800">
              {totalIndemnites} €
            </div>
            <div className="text-xs text-slate-500 mt-1">
              Le total général sera recalculé côté serveur (frais + trajet + indemnité).
            </div>
          </div>
        </div>

        {/* ✅ Commentaire */}
        <div className="md:col-span-2">
          <Field
            label="Commentaire (optionnel)"
            value={draft?.commentaire ?? ""}
            onChange={(v) => setDraft((p) => ({ ...p, commentaire: v }))}
            placeholder="Remarques, précisions..."
          />
        </div>
      </div>
    </SectionCard>
  );
}
